/**
 * MediaPanel — Feb 2026.
 *
 * Unified per-project media workstation for videos + photos.
 *
 *  - Videos land in Bunny Stream via TUS (existing flow).
 *  - Photos land in Bunny Storage via a single browser PUT (new).
 *    Downloads use Bunny Optimizer query params for on-the-fly resize.
 *
 * Card branches ("video" vs "photo") share the AssetCard shell so the
 * grid stays visually consistent; the play button + duration only
 * render on the video branch, and photos get a resolution badge
 * (4K / HD / SD) based on width_px.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Camera,
  Clock,
  Download,
  Film,
  Image as ImageIcon,
  Loader2,
  Play,
  RefreshCw,
  Trash2,
  Upload,
  Video as VideoIcon,
} from "lucide-react";
import * as tus from "tus-js-client";
import { videosApi, photosApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import VideoPlayerModal from "@/components/VideoPlayerModal";
import AssetDetailModal from "@/components/AssetDetailModal";

const MAX_VIDEO_BYTES = 5 * 1024 * 1024 * 1024; // 5 GB
const MAX_PHOTO_BYTES = 100 * 1024 * 1024; // 100 MB

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDuration(sec) {
  if (!sec || sec <= 0) return "";
  const s = Math.floor(sec % 60);
  const m = Math.floor((sec / 60) % 60);
  const h = Math.floor(sec / 3600);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function resolutionBadge(widthPx) {
  if (!widthPx) return null;
  if (widthPx >= 3840) return "4K";
  if (widthPx >= 1920) return "HD";
  if (widthPx >= 1280) return "720p";
  return "SD";
}

export default function MediaPanel({ projectId, projectName, myRole }) {
  const { user: me } = useAuth();
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const inputRef = useRef(null);

  const effectiveRole = myRole || me?.role || "viewer";
  const isAdmin = me?.role === "admin" || effectiveRole === "admin";
  const canUpload =
    isAdmin ||
    ["editor", "contributor", "manager"].includes(effectiveRole);
  // Downloads mirror upload threshold (contributor+). Viewers can preview
  // photos/videos in the modal but cannot pull the originals.
  const canDownload = canUpload;

  const load = async () => {
    try {
      const [vids, pics] = await Promise.all([
        videosApi.list(projectId).catch(() => []),
        photosApi.list(projectId).catch(() => []),
      ]);
      setVideos(vids || []);
      setPhotos(pics || []);
    } catch {
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    load();
     
  }, [projectId]);

  // Poll processing videos every 5s until they're ready or fail.
  useEffect(() => {
    const stillProcessing = videos.filter((v) => v.status === "processing");
    if (stillProcessing.length === 0) return;
    const t = setInterval(async () => {
      const updated = await Promise.all(
        stillProcessing.map((v) => videosApi.refresh(v.id).catch(() => null)),
      );
      const changes = updated.filter(Boolean);
      if (changes.length) {
        setVideos((prev) =>
          prev.map((v) => {
            const found = changes.find((c) => c.id === v.id);
            return found || v;
          }),
        );
      }
    }, 5000);
    return () => clearInterval(t);
  }, [videos]);

  const uploadPhoto = async (file, idx, total) => {
    // Init reserves the row + returns the direct PUT URL
    const init = await photosApi.uploadInit({
      project_id: projectId,
      filename: file.name,
      mime: file.type || "image/jpeg",
      size_bytes: file.size,
      title: file.name.replace(/\.[^.]+$/, ""),
    });
    // Single PUT to Bunny Storage — browser talks to Bunny directly
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(init.method || "PUT", init.upload_url);
      Object.entries(init.headers || {}).forEach(([k, v]) => {
        xhr.setRequestHeader(k, v);
      });
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const fileFraction = e.loaded / e.total;
        const batchFraction = (idx + fileFraction) / total;
        setProgress(Math.round(batchFraction * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Bunny PUT failed: HTTP ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.send(file);
    });
    // Confirm — backend probes dimensions + flips the row to ready
    return photosApi.confirm({
      project_id: projectId,
      photo_id: init.photo_id,
    });
  };

  const uploadVideo = async (file, idx, total) => {
    const title = file.name.replace(/\.[^.]+$/, "");
    const init = await videosApi.tusInit(projectId, title);
    await new Promise((resolve, reject) => {
      const upload = new tus.Upload(file, {
        endpoint: init.tus_endpoint,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: {
          AuthorizationSignature: init.authorization_signature,
          AuthorizationExpire: String(init.authorization_expire),
          VideoId: init.video_guid,
          LibraryId: String(init.library_id),
        },
        metadata: {
          filetype: file.type || "video/mp4",
          title,
          collection: init.collection_guid || "",
        },
        chunkSize: 20 * 1024 * 1024,
        onError: (err) => reject(err),
        onProgress: (bytesSent, bytesTotal) => {
          const fileFraction = bytesTotal ? bytesSent / bytesTotal : 0;
          const batchFraction = (idx + fileFraction) / total;
          setProgress(Math.round(batchFraction * 100));
        },
        onSuccess: () => resolve(),
      });
      upload.start();
    });
    return videosApi.tusComplete({
      project_id: projectId,
      bunny_guid: init.video_guid,
      title,
      filename: file.name,
      mime: file.type || "video/mp4",
    });
  };

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    // Route each file to its lane (video vs photo). Anything else is skipped.
    const valid = [];
    for (const f of files) {
      const isVideo = (f.type || "").startsWith("video/");
      const isImage = (f.type || "").startsWith("image/");
      if (!isVideo && !isImage) {
        toast.error(`${f.name}: not an image or video, skipped`);
        continue;
      }
      const cap = isVideo ? MAX_VIDEO_BYTES : MAX_PHOTO_BYTES;
      if (f.size > cap) {
        toast.error(
          `${f.name}: ${formatBytes(f.size)} — max ${formatBytes(cap)}, skipped`,
        );
        continue;
      }
      valid.push({ file: f, kind: isVideo ? "video" : "photo" });
    }
    if (!valid.length) {
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setUploading(true);
    setProgress(0);
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < valid.length; i++) {
      const { file, kind } = valid[i];
      try {
        if (kind === "photo") {
          const created = await uploadPhoto(file, i, valid.length);
          setPhotos((prev) => [created, ...prev]);
        } else {
          const created = await uploadVideo(file, i, valid.length);
          setVideos((prev) => [created, ...prev]);
        }
        ok += 1;
      } catch (err) {
        failed += 1;
        let detail =
          err?.response?.data?.detail ||
          err?.originalResponse?.getBody?.() ||
          err?.message ||
          "upload failed";
        if (err?.originalResponse?.getStatus) {
          detail = `Bunny HTTP ${err.originalResponse.getStatus()}: ${detail}`;
        }
        toast.error(`${file.name}: ${detail}`);
      }
    }
    if (ok > 0) {
      toast.success(
        ok === 1
          ? `${valid[0].file.name} uploaded`
          : `${ok} files uploaded${failed ? ` (${failed} failed)` : ""}`,
      );
    }
    setUploading(false);
    setProgress(0);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDeleteVideo = async (v) => {
    if (!window.confirm(`Delete "${v.title}"? It will be removed from Bunny too.`))
      return;
    try {
      await videosApi.remove(v.id);
      setVideos((prev) => prev.filter((x) => x.id !== v.id));
      toast.success("Video removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  const handleDeletePhoto = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? It will be removed from Bunny Storage.`))
      return;
    try {
      await photosApi.remove(p.id);
      setPhotos((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Photo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  const canDeleteVideo = (v) => {
    if (!me) return false;
    return (
      isAdmin ||
      (["editor", "manager"].includes(effectiveRole) && v.uploader_id === me.id)
    );
  };

  const canDeletePhoto = (p) => {
    if (!me) return false;
    return isAdmin || p.uploader_id === me.id;
  };

  // Merge into a single grid ordered by created_at desc
  const assets = [
    ...videos.map((v) => ({ ...v, __kind: "video" })),
    ...photos.map((p) => ({ ...p, __kind: "photo" })),
  ].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));

  return (
    <div className="space-y-6" data-testid="media-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-overline text-amber-500/80 mb-1 flex items-center gap-1.5">
            <ImageIcon className="w-3 h-3" /> Media
          </div>
          <h2 className="font-heading text-2xl font-light text-zinc-100 leading-tight">
            Project media workstation
          </h2>
          <p className="text-sm text-zinc-500 mt-1 max-w-xl">
            Videos and photos live in one place. Drop stills or clips
            here — videos land in the{" "}
            <span className="font-mono text-amber-400">incoming</span> folder,
            photos go to Bunny Storage.
          </p>
        </div>
        {canUpload && (
          <div>
            <input
              ref={inputRef}
              type="file"
              accept="video/*,image/*"
              multiple
              className="hidden"
              data-testid="media-upload-input"
              onChange={(e) => handleUpload(e.target.files)}
              disabled={uploading}
            />
            <button
              type="button"
              data-testid="media-upload-btn"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 rounded-sm px-4 py-2 text-sm font-medium flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading… {progress}%
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload media
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {uploading && (
        <div
          className="h-1 w-full bg-zinc-900 rounded-sm overflow-hidden"
          data-testid="media-upload-progress"
        >
          <div
            className="h-full bg-amber-500 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading media…
        </div>
      ) : (
        <div
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            if (canUpload) handleUpload(e.dataTransfer.files);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (canUpload && !dragActive) setDragActive(true);
          }}
          onDragLeave={(e) => {
            if (e.currentTarget === e.target) setDragActive(false);
          }}
          className={`rounded-sm transition-colors ${
            dragActive ? "ring-2 ring-amber-500/60 bg-amber-500/5 p-1" : ""
          }`}
        >
          {assets.length === 0 ? (
            <div
              data-testid="media-empty"
              className="border border-dashed border-zinc-800 rounded-md bg-zinc-950/60 p-12 text-center"
            >
              <Film className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <h3 className="font-heading text-lg font-light text-zinc-300 mb-1">
                No media yet
              </h3>
              <p className="text-xs text-zinc-500">
                {canUpload
                  ? "Click Upload or drag videos and photos here."
                  : "Uploads will appear here once the team drops them in."}
              </p>
            </div>
          ) : (
            <>
              {canUpload && (
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 text-center py-2 border border-dashed border-zinc-800/60 rounded-sm mb-3">
                  Drop videos or photos here
                </div>
              )}
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                data-testid="media-grid"
              >
                {assets.map((a) =>
                  a.__kind === "video" ? (
                    <AssetCard
                      key={`v-${a.id}`}
                      kind="video"
                      asset={a}
                      canDownload={canDownload}
                      onOpen={() => a.status === "ready" && setPlaying(a)}
                      onDelete={
                        canDeleteVideo(a) ? () => handleDeleteVideo(a) : null
                      }
                    />
                  ) : (
                    <AssetCard
                      key={`p-${a.id}`}
                      kind="photo"
                      asset={a}
                      canDownload={canDownload}
                      onOpen={() => setViewingPhoto(a)}
                      onDelete={
                        canDeletePhoto(a) ? () => handleDeletePhoto(a) : null
                      }
                    />
                  ),
                )}
              </div>
            </>
          )}
        </div>
      )}

      {playing && (
        <VideoPlayerModal
          video={playing}
          projectName={projectName}
          canDownload={canDownload}
          onClose={() => setPlaying(null)}
        />
      )}
      {viewingPhoto && (
        <AssetDetailModal
          photo={viewingPhoto}
          projectName={projectName}
          canDownload={canDownload}
          onClose={() => setViewingPhoto(null)}
        />
      )}
    </div>
  );
}

function AssetCard({ kind, asset, canDownload = true, onOpen, onDelete }) {
  const isVideo = kind === "video";
  const isReady = isVideo ? asset.status === "ready" : true;
  const isProcessing = isVideo && asset.status === "processing";
  const isFailed = isVideo && asset.status === "failed";
  const [thumbErrored, setThumbErrored] = useState(false);
  const badge = !isVideo ? resolutionBadge(asset.width_px) : null;
  // Photo thumbnails come pre-decorated from the backend: when Bunny
  // Optimizer is OFF (demo mode) this is the raw pull-zone URL, which
  // the browser scales down for the grid — a few full-res loads is
  // fine at demo scale. When Optimizer is turned on later, the same
  // field starts carrying ``?width=600&quality=70`` for free.
  const thumbSrc = asset.thumbnail_url;

  return (
    <div
      data-testid={`asset-card-${asset.id}`}
      className="group bg-zinc-950/60 border border-zinc-800/60 rounded-md overflow-hidden hover:border-amber-500/40 transition-colors flex flex-col"
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={isVideo && !isReady}
        data-testid={`asset-open-${asset.id}`}
        className="relative aspect-video bg-zinc-900 w-full overflow-hidden group/thumb disabled:cursor-not-allowed"
      >
        {thumbSrc && !thumbErrored ? (
          <img
            src={thumbSrc}
            alt={asset.title}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setThumbErrored(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-900 to-zinc-950">
            {isVideo ? (
              <Film className="w-10 h-10 text-zinc-700" />
            ) : (
              <Camera className="w-10 h-10 text-zinc-700" />
            )}
          </div>
        )}
        {isVideo && isReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/thumb:bg-black/50 transition-colors">
            <Play
              className="w-12 h-12 text-white/0 group-hover/thumb:text-white transition-colors"
              fill="currentColor"
            />
          </div>
        )}
        {!isVideo && canDownload && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/thumb:bg-black/40 transition-colors">
            <Download className="w-9 h-9 text-white/0 group-hover/thumb:text-white transition-colors" />
          </div>
        )}
        {isProcessing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-amber-400">
            <RefreshCw className="w-6 h-6 animate-spin mb-1.5" />
            <span className="text-[11px] font-mono uppercase tracking-wide">
              Encoding…
            </span>
          </div>
        )}
        {isFailed && (
          <div className="absolute inset-0 flex items-center justify-center bg-rose-950/70 text-rose-300 text-xs font-mono uppercase">
            Encoding failed
          </div>
        )}
        {isVideo && isReady && asset.duration_sec > 0 && (
          <span className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[10px] font-mono px-1.5 py-0.5 rounded-sm">
            {formatDuration(asset.duration_sec)}
          </span>
        )}
        {badge && (
          <span
            data-testid={`asset-badge-${asset.id}`}
            className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[10px] font-mono px-1.5 py-0.5 rounded-sm tracking-wider"
          >
            {badge}
          </span>
        )}
        {!isVideo && (
          <span
            className="absolute top-1.5 left-1.5 bg-zinc-950/80 border border-zinc-800 text-zinc-400 text-[9px] font-mono px-1.5 py-0.5 rounded-sm uppercase tracking-wider flex items-center gap-1"
            title="Photo"
          >
            <ImageIcon className="w-2.5 h-2.5" /> Photo
          </span>
        )}
      </button>

      <div className="p-3 flex-1 flex flex-col gap-1.5">
        <div className="flex items-start justify-between gap-2">
          <h4
            className="text-sm text-zinc-100 font-medium leading-snug line-clamp-2 flex-1"
            title={asset.title}
          >
            {asset.title}
          </h4>
          {onDelete && (
            <button
              type="button"
              data-testid={`asset-delete-${asset.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="text-zinc-600 hover:text-rose-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              aria-label="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 uppercase tracking-wide">
          {isVideo ? <VideoIcon className="w-2.5 h-2.5" /> : <ImageIcon className="w-2.5 h-2.5" />}
          <Clock className="w-2.5 h-2.5" />
          <span>{timeAgo(asset.created_at)}</span>
          {asset.uploader_name && (
            <>
              <span className="text-zinc-700">·</span>
              <span className="truncate normal-case tracking-normal">
                {asset.uploader_name}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
